import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { SectionLayout, StandardDropdown } from '../common/designSystem';
import { fetchHubFilterData } from '../../api/adminDashboardApi';
import { adminReportApi } from '../../api/adminReportApi';
import { hubTabs } from '../../utils/adminHubs';
import { isSuperAndSubAdmin } from '../../utils/helper';

/**
 * Shell for every super-admin feature hub.
 *
 * One component rather than nineteen near-identical layout files: the tabs,
 * the guard, the filter wiring and the manage dropdown are the same for all of
 * them, and the only thing that differs is which hub it is showing — which is
 * a lookup in utils/adminHubs.js.
 *
 * The "Manage content" dropdown is how a super admin reaches the library
 * behind a feature (the meditations themselves, the sleep sounds, the habit
 * definitions). It sits beside the filter rather than in the tab row because
 * editing the catalogue is a different job from reading the analytics, and
 * because a feature can own several content pages — more than a tab row can
 * take without wrapping.
 *
 * A hub with no `manage` entries simply does not render the dropdown. Sleep's
 * tracker is the case that proves the point: it reports what people recorded
 * rather than anything Shoorah publishes.
 */
const AdminHubLayout = ({ hub, children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSuperAndSubAdmin()) {
      navigate('/login', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const manage = hub.manage || [];

  return (
    <SectionLayout
      title={hub.title}
      description={hub.description}
      tabs={hubTabs(hub)}
      filterable
      loadFilterData={fetchHubFilterData}
      /**
       * A hub whose `key` matches the backend report manifest gets a download
       * button. Reports raised here are platform-wide; a company's or a
       * person's report is downloaded from their own page, where the subject
       * is unambiguous.
       */
      reportFeatureKey={hub.key}
      reportApi={adminReportApi}
      action={
        manage.length > 0 ? (
          <StandardDropdown
            // The label never becomes the selected option's name: this is a
            // menu of places to go, not a value the page holds, so `value` is
            // deliberately left unmatched.
            label="Manage content"
            options={manage.map((item) => ({ label: item.label, value: item.path }))}
            value={null}
            onChange={(path) => navigate(path)}
            variant="secondary"
            align="right"
          />
        ) : null
      }
    >
      {children}
    </SectionLayout>
  );
};

AdminHubLayout.propTypes = {
  hub: PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    basePath: PropTypes.string.isRequired,
    manage: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, path: PropTypes.string }))
  }).isRequired,
  children: PropTypes.node
};

/**
 * `withHub(hub)(Page)` — the route table's wrapper, so a hub's six routes are
 * six one-liners rather than six copies of the layout JSX.
 */
export const withHub = (hub) => (Component) =>
  function HubRoute() {
    return (
      <AdminHubLayout hub={hub}>
        <Component />
      </AdminHubLayout>
    );
  };

export default AdminHubLayout;
